import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CustomFieldContext,
  GetAssignmentAssetsQuery,
  ShipmentLocationType,
  Status,
  TripAssetTypes,
  useGetCustomFieldDefinitionsQuery,
  useGetOrganizationSettingsQuery,
} from "../../../graphql/generated";
import {
  AugmentedDriver,
  AugmentedShipment,
  PlanningToolProps,
} from "./PlanningTool";
import LynksTable from "../../common/LynksTable";
import { EnumTableField, TableField } from "../../common/LynksTable/LynksTable";
import {
  renderExtraLocations,
  shipmentStatusColors,
} from "../../shipment/ShipmentsList/ShipmentsList";
import {
  formatDateTimeForLocation,
  formatDurationClock,
  formatTimeForLocation,
} from "../../../utils/labels/formatDateTime";
import { first, keyBy, last, sum } from "lodash";
import { shipmentLocationLabel } from "../../../utils/labels/shipmentLocationLabel";
import {
  DragEvent,
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatMileage } from "../../../utils/labels/formatDistance";
import { differenceInSeconds } from "date-fns";
import goodUnitLabel from "../../../utils/labels/goodUnitsLabel";
import { Feature, useFeature } from "../../account/Access/FeatureGuard";
import { formatShipmentLabel } from "../../../utils/labels/shipmentLabel";
import { CallSplit, Merge } from "@mui/icons-material";
import { AugmentedShipmentDetails } from "./PlanningCalendar";
import TripViolations from "../../trip/TripDetails/TripViolations";
import { useTranslation } from "react-i18next";
import statusLabel from "../../../utils/labels/statusLabel";
import LoadSplittingModalContainer from "../../shipment/LoadSplittingModal";
import TripSeparationModal from "../../../views/trip-planning/TripSeparationModal";

import { useGetGroupsQuery } from "../../../graphql/generated";
import AssetLink from "../../asset-management/AssetLink/AssetLink";
import { TripEtaStatusDot } from "./TripEtaStatusDot";
import { testIds } from "./PlanningGrid.testIds";

export type PlanningGridProps = {
  drivers: AugmentedDriver[];
  tractors: GetAssignmentAssetsQuery["tractors"]["data"];
  trailers: GetAssignmentAssetsQuery["trailers"]["data"];
  filteredShipments: AugmentedShipment[];
  onShipmentsSelect: (shipments: AugmentedShipment[]) => void;
  onShipmentsChange: (shipments: AugmentedShipment[]) => void;
  onShipmentsCombine: (shipments: AugmentedShipment[]) => void;
  onShipmentsUncombine: (shipments: AugmentedShipment[]) => void;
  onFilteredShipmentsChange: (shipments: AugmentedShipment[]) => void;
  onRefresh: PlanningToolProps["onRefresh"];
  onSplitApplied: PlanningToolProps["onSplitApplied"];
};

type ShipmentItem = PlanningGridProps["filteredShipments"][0];

const PlanningGrid: React.FC<PlanningGridProps> = memo(
  ({
    drivers,
    tractors,
    trailers,
    filteredShipments,
    onShipmentsSelect,
    onShipmentsCombine,
    onShipmentsUncombine,
    onRefresh,
    onSplitApplied,
    onFilteredShipmentsChange,
  }) => {
    const { t } = useTranslation(["orders", "trips", "common", "planning"]);
    const [selectedShipmentIds, setSelectedShipmentIds] = useState<string[]>(
      []
    );

    const groupsQuery = useGetGroupsQuery();
    const groups = groupsQuery.data?.groups.data;

    const groupMap = useMemo(() => keyBy(groups, (g) => g._id), [groups]);

    const [openedShipmentId, setOpenedShipmentId] =
      useState<string | null>(null);

    const [loadSplittingModalOpen, setLoadSplittingModalOpen] = useState(false);
    const [selectedShipmentForSplitting, setSelectedShipmentForSplitting] =
      useState<AugmentedShipment | null>(null);

    const [tripSeparationModalOpen, setTripSeparationModalOpen] =
      useState(false);
    const [shipmentForSeparation, setShipmentForSeparation] =
      useState<AugmentedShipment | null>(null);

    const commodityManagementFeature = useFeature(Feature.CommodityManagement);
    const orgSettingsQuery = useGetOrganizationSettingsQuery();
    const etaEnabled =
      !!orgSettingsQuery.data?.organizationSettings?.dispatching?.enableEta;

    const statusField: EnumTableField<ShipmentItem, Status> = useMemo(
      () => ({
        value: (shipment) =>
          shipment.recurrence ? null : shipment.trip?.status || shipment.status,
        label: t("status.label", "Status"),
        type: "enum",
        colors: shipmentStatusColors,
        values: Object.values(Status),
        valueLabel: statusLabel,
        sortBy: "status",
      }),
      [t]
    );

    const shipmentFields: TableField<ShipmentItem, Status>[] = useMemo(
      () => [
        {
          value: (shipment) =>
            renderForAugmentedShipment(shipment, (shipment) => {
              const label = formatShipmentLabel(shipment);
              return (
                <Typography>
                  {label.charAt(0).toUpperCase() + label.slice(1)}
                </Typography>
              );
            }),
          label: t("orderNumber"),
          type: "number",
          sortBy: "shipmentNumber",
          rawValue: (shipment) =>
            parseInt(shipment.shipmentNumber?.replace(/^\D+/, "") || "0", 10),
        },
        {
          value: (shipment) => (
            <Typography>{shipment.trip?.tripNumber || ""}</Typography>
          ),
          label: t("tripNumber"),
          type: "number",
          sortBy: "trip.tripNumber",
        },
        {
          value: (shipment) =>
            sum(
              [shipment, ...shipment.otherShipmentsInTrip]
                .flatMap((shipment) => shipment.shipmentLocations)
                .flatMap((loc) =>
                  loc.shippedGoods?.map((good) => good.quantity)
                )
            ),
          label: t("totalVolume"),
          type: "number",
          sortBy: "shipmentLocations.0.shippedGoods.0.quantity",
        },
        {
          value: (shipment) =>
            renderForAugmentedShipment(shipment, (shipment) =>
              shipment.shipmentLocations.flatMap((location) =>
                location.shippedGoods?.map((good) => (
                  <Typography>
                    {good.label} - {good.quantity}
                    {goodUnitLabel(good.unit)}
                  </Typography>
                ))
              )
            ),
          label: t("commodities"),
          type: "string",
          sortBy: "shipmentLocations.0.shippedGoods.0.label",
        },
        statusField,
        {
          value: (shipment) => shipment.trip?.nextStopName || "",
          label: t("planning:nextStop", "Next Stop"),
          type: "string",
        },
        ...(etaEnabled
          ? ([
              {
                value: (shipment) =>
                  shipment.trip?.etaToNextStopMinutes != null
                    ? t("planning:etaMinutes", "{{count}} min", {
                        count: shipment.trip.etaToNextStopMinutes,
                      })
                    : "",
                label: t("planning:etaToNextStop", "ETA To Next Stop"),
                type: "string",
              },
              {
                value: (shipment) =>
                  shipment.trip?.etaToDeliveryMinutes != null
                    ? t("planning:etaMinutes", "{{count}} min", {
                        count: shipment.trip.etaToDeliveryMinutes,
                      })
                    : "",
                label: t("planning:etaToDelivery", "ETA To Delivery"),
                type: "string",
              },
              {
                value: (shipment) =>
                  shipment.trip?.etaStatus ? (
                    <TripEtaStatusDot etaStatus={shipment.trip.etaStatus} />
                  ) : null,
                label: t("planning:etaStatus", "ETA Status"),
                type: "custom",
              },
            ] as TableField<ShipmentItem, Status>[])
          : []),
        {
          value: (shipment) => (
            <Typography variant="body1">
              {formatDateTimeForLocation(
                shipment.trip?.firstPickupTime ||
                  shipment.route?.firstPickupTime,
                shipment.route?.locations?.[0]
              )}
            </Typography>
          ),
          subtitle: (shipment) => {
            return `${formatTimeForLocation(
              shipment.earliestPickup,
              shipment.route?.locations?.[0],
              false
            )} - ${formatTimeForLocation(
              shipment.latestPickup,
              shipment.route?.locations?.[0],
              false
            )}`;
          },
          label: t("pickupDate"),
          type: "datetime",
          sortBy: "route.firstPickupTime",
        },
        {
          value: (shipment) => (
            <Typography variant="body1">
              {formatDateTimeForLocation(
                shipment.trip?.lastDropoffTime ||
                  shipment.route?.lastDropoffTime,
                last(shipment.route?.locations)
              )}
            </Typography>
          ),
          subtitle: (shipment) => {
            return `${formatTimeForLocation(
              shipment.earliestDropoff,
              last(shipment.route?.locations),
              false
            )} - ${formatTimeForLocation(
              shipment.latestDropoff,
              last(shipment.route?.locations),
              false
            )}`;
          },
          label: t("deliveryDate"),
          type: "datetime",
          sortBy: "route.lastDropoffTime",
        },
        {
          value: (shipment) => (
            <Typography variant="body1">{shipment.customer.name}</Typography>
          ),
          label: t("customer", "Customer"),
          type: "string",
          sortBy: "customer.name",
        },
        {
          value: (shipment) =>
            renderForAugmentedShipment(shipment, (shipment) => (
              <Typography>
                {shipmentLocationLabel(first(shipment.route?.locations))}
              </Typography>
            )),
          extra: (shipment) => {
            return renderExtraLocations(shipment, ShipmentLocationType.Pickup);
          },
          label: t("shipper"),
          type: "string",
          sortBy: "route.locations.0.shipper.name",
        },
        {
          value: (shipment) =>
            renderForAugmentedShipment(shipment, (shipment) => (
              <Typography>
                {[shipment.originCity, shipment.originState]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )),
          label: t("shipperAddress"),
          type: "string",
          sortBy: "originCity",
        },
        {
          value: (shipment) =>
            renderForAugmentedShipment(shipment, (shipment) => (
              <Typography>
                {shipmentLocationLabel(last(shipment.route?.locations))}
              </Typography>
            )),
          extra: (shipment) => {
            return renderExtraLocations(shipment, ShipmentLocationType.DropOff);
          },
          label: t("consigneeReceiver"),
          type: "string",
          sortBy: "route.locations.1.receiver.name",
        },
        {
          value: (shipment) =>
            renderForAugmentedShipment(shipment, (shipment) => (
              <Typography>
                {[shipment.destinationCity, shipment.destinationState]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )),
          label: t("receiverAddress"),
          type: "string",
          sortBy: "destinationCity",
        },
        {
          value: (shipment) =>
            formatMileage(
              shipment.trip?.routeDistance || shipment.route?.routeDistance || 0
            ),
          label: t("distance"),
          type: "number",
          sortBy: "trip.routeDistance",
        },
        {
          value: (shipment) => (
            <Typography>
              {formatDurationClock(
                differenceInSeconds(
                  new Date(
                    shipment.trip?.lastDropoffTime ||
                      shipment.route?.lastDropoffTime
                  ),
                  new Date(
                    shipment.trip?.firstPickupTime ||
                      shipment.route?.firstPickupTime
                  )
                )
              )}
            </Typography>
          ),
          label: t("duration"),
          type: "number",
          sortBy: "trip.duration",
        },
        {
          value: (shipment) =>
            `$ ${sum(
              [shipment, ...shipment.otherShipmentsInTrip]
                .flatMap((s) => s.charges)
                .map((charge) => charge.total)
            ).toFixed(2)}`,
          label: t("revenue"),
          type: "number",
          sortBy: "charges[0].total",
        },
        {
          value: (shipment) => {
            const driver = shipment.trip?.driverEntity;
            const carrier = shipment.trip?.carrierEntity;
            const asset = driver || carrier;

            return asset ? (
              <AssetLink
                asset={asset}
                assetType={
                  driver ? TripAssetTypes.Driver : TripAssetTypes.Carrier
                }
              />
            ) : null;
          },
          label: `${t("trips:driver")} / ${t("trips:carrier", "Carrier")}`,
          type: "string",
          sortBy: [
            "trip.driverEntity.firstname",
            "trip.driverEntity.lastname",
            "trip.carrierEntity.name",
          ],
        },
        {
          value: (shipment) => {
            const tractor = shipment.trip?.tractorEntity;
            return tractor ? (
              <AssetLink asset={tractor} assetType={TripAssetTypes.Tractor} />
            ) : null;
          },
          label: t("trips:tractor"),
          type: "string",
          sortBy: "trip.tractorEntity.serialNumber",
        },
        {
          value: (shipment) => {
            const trailer = shipment.trip?.trailerEntity;
            return trailer ? (
              <AssetLink asset={trailer} assetType={TripAssetTypes.Trailer} />
            ) : null;
          },
          label: t("trips:trailer"),
          type: "string",
          sortBy: "trip.trailerEntity.serialNumber",
        },
        {
          value: (shipment) => {
            return shipment.trip ? (
              <TripViolations trip={shipment.trip} />
            ) : null;
          },
          label: t("trips:violations"),
          type: "custom",
        },
        {
          value: (shipment) => (
            <Stack direction="row" spacing={1}>
              {shipment.tags?.map((tag) => (
                <Chip label={tag} />
              ))}
            </Stack>
          ),
          sortBy: "tags",
          label: t("common:tags"),
          type: "string",
        },
        {
          value: (shipment) =>
            shipment?.groupIds?.map((id) => groupMap[id]?.name) || [],
          label: t("common:groups"),
          type: "select",
          values: groups?.map((g) => g.name),
        },
        {
          value: (shipment) => shipment.billOfLadingNumbers?.join(", ") || "",
          label: t("orders:referenceNumberType.BILL_OF_LADING_NUMBER"),
          type: "string",
          sortBy: "billOfLadingNumbers",
          hidden: true,
        },
        {
          value: (shipment) => shipment.purchaseOrderNumbers?.join(", ") || "",
          label: t("orders:referenceNumberType.PURCHASE_ORDER_NUMBER"),
          type: "string",
          sortBy: "purchaseOrderNumbers",
          hidden: true,
        },
        {
          value: (shipment) => shipment.ticketNumbers?.join(", ") || "",
          label: t("orders:referenceNumberType.TICKET_NUMBER"),
          type: "string",
          key: "ticketNumbers",
          hidden: true,
        },
        {
          value: (shipment) => shipment.rateConNumbers?.join(", ") || "",
          label: t("orders:referenceNumberType.RATE_CON_NUMBER"),
          type: "string",
          sortBy: "rateConNumbers",
          hidden: true,
        },
      ],
      [t, statusField, groups, groupMap, etaEnabled]
    );

    const filteredShipmentFields = useMemo(
      () =>
        shipmentFields.filter((field) => {
          if (commodityManagementFeature.isAvailable) {
            return true;
          }
          return field.label !== "Total Volume" && field.label !== "Commodity";
        }),
      [commodityManagementFeature.isAvailable, shipmentFields]
    );

    const prevFilteredShipmentsLength = useRef(filteredShipments.length);

    useEffect(() => {
      if (prevFilteredShipmentsLength.current !== filteredShipments.length) {
        // Preserve selections that still exist in the new data (e.g., after combine)
        const currentIds = new Set(filteredShipments.map((s) => s._id));
        setSelectedShipmentIds((prev) => prev.filter((id) => currentIds.has(id)));
        prevFilteredShipmentsLength.current = filteredShipments.length;
      }
    }, [filteredShipments.length, filteredShipments]);

    const selectedShipments = useMemo(
      () =>
        filteredShipments.filter((s) => selectedShipmentIds.includes(s._id)),
      [filteredShipments, selectedShipmentIds]
    );

    const openedShipment = useMemo(
      () => filteredShipments.find((s) => s._id === openedShipmentId),
      [openedShipmentId, filteredShipments]
    );

    const combineSelectedShipments = () => {
      const shipmentsToCombine = selectedShipments;
      onShipmentsCombine(shipmentsToCombine);
    };

    const canUncombine = useMemo(() => {
      if (selectedShipments.length !== 1) {
        return false;
      }
      const shipment = selectedShipments[0];
      return (shipment.otherShipmentsInTrip?.length ?? 0) > 0;
    }, [selectedShipments]);

    const uncombineSelectedShipment = () => {
      if (!canUncombine) {
        return;
      }
      const shipment = selectedShipments[0];
      setShipmentForSeparation(shipment);
      setTripSeparationModalOpen(true);
    };

    const onSelect = useCallback(
      (records: AugmentedShipment[]) => {
        setSelectedShipmentIds(records.map((r) => r._id));
        onShipmentsSelect(records);
      },
      [onShipmentsSelect]
    );

    const onDragStart = useCallback(
      (_: DragEvent<HTMLDivElement>, shipment: AugmentedShipment) => {
        setTimeout(() => {
          // setSelectedShipmentIds([shipment._id]);
          onShipmentsSelect([shipment]);
        });
      },
      [onShipmentsSelect]
    );

    const onDragEnd = useCallback(
      (_: DragEvent<HTMLDivElement>, shipment: AugmentedShipment) => {
        setTimeout(() => {
          // setSelectedShipmentIds([]);
          onShipmentsSelect([]);
        });
      },
      [onShipmentsSelect]
    );

    const onRecordClick = useCallback((shipment: AugmentedShipment) => {
      setOpenedShipmentId(shipment._id);
    }, []);

    const onFilteredRecordsChange = useCallback(
      (shipments: AugmentedShipment[]) => {
        onFilteredShipmentsChange(shipments);
      },
      [onFilteredShipmentsChange]
    );

    const rowStyle = useCallback(
      (shipment: AugmentedShipment): SxProps<Theme> => {
        const hasViolations = !!shipment.trip?.violations?.length;
        return (theme) => ({
          backgroundColor: hasViolations
            ? theme.palette.warning.light
            : undefined,
        });
      },
      []
    );

    const customFieldDefinitionsQuery = useGetCustomFieldDefinitionsQuery();

    const customFieldDefinitions =
      customFieldDefinitionsQuery.data?.cutomFieldDefinitions.data
        ?.filter((customFieldDefinition) =>
          customFieldDefinition.context.includes(CustomFieldContext.Shipment)
        )
        ?.filter(
          (def, index, arr) => arr.findIndex((d) => d.key === def.key) === index
        );

    if (customFieldDefinitionsQuery.isLoading || groupsQuery.isLoading) {
      return null;
    }

    return (
      <Box
        sx={{
          position: "relative",
          height: "calc(100% - 10px)",
        }}
      >
        <Box
          sx={{
            height: "100%",
            overflow: "auto",
          }}
        >
          <LynksTable
            data={filteredShipments}
            fields={filteredShipmentFields}
            compact
            selected={selectedShipments}
            onSelect={onSelect}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            draggable
            onRecordClick={onRecordClick}
            onFilteredRecordsChange={onFilteredRecordsChange}
            stickyHeader
            rowStyle={rowStyle}
            rowDataAttributes={(shipment) => ({
              "data-shipment-id": shipment._id,
              "data-shipment-number": shipment.shipmentNumber || "",
            })}
            id="planning-grid-2"
            customFieldDefinitions={customFieldDefinitions}
            actions={[
              {
                icon: <CallSplit />,
                label: "Split",
                tooltip: "Split Order",
                disabled: (row) => row.status === Status.Complete,
                onClick: (row) => {
                  setSelectedShipmentForSplitting(row);
                  setLoadSplittingModalOpen(true);
                },
              },
            ]}
          />
        </Box>
        {selectedShipmentIds.length >= 2 ? (
          <Tooltip title={t("combineOrdersTooltip")}>
            <Fab data-testid={testIds.planningGridCombineOrders}
              sx={{
                position: "absolute",
                bottom: 32,
                right: 16,
              }}
              variant="extended"
              color="secondary"
              onClick={combineSelectedShipments}
            >
              <Merge />
            </Fab>
          </Tooltip>
        ) : null}
        {canUncombine ? (
          <Tooltip
            title={t(
              "planning:uncombineOrdersTooltip",
              "Separate combined orders back into individual orders",
            )}
          >
            <Fab data-testid={testIds.planningGridUncombineOrders}
              sx={{
                position: "absolute",
                bottom: 32,
                right: 16,
              }}
              variant="extended"
              color="secondary"
              onClick={uncombineSelectedShipment}
            >
              <CallSplit />
            </Fab>
          </Tooltip>
        ) : null}
        <Dialog
          open={!!openedShipmentId}
          onClose={() => setOpenedShipmentId(null)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>{t("orderDetails")}</DialogTitle>
          <DialogContent>
            {openedShipment && (
              <AugmentedShipmentDetails
                shipment={openedShipment}
                onRefresh={(shipmentIds) => {
                  setOpenedShipmentId(null);
                  return onRefresh(shipmentIds);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {selectedShipmentForSplitting ? (
          <LoadSplittingModalContainer
            open={loadSplittingModalOpen}
            onClose={() => {
              setLoadSplittingModalOpen(false);
            }}
            onSplitComplete={async ({ parentId, childIds }) => {
              await onSplitApplied(parentId, childIds);
            }}
            shipment={selectedShipmentForSplitting}
          />
        ) : null}

        {shipmentForSeparation ? (
          <TripSeparationModal
            open={tripSeparationModalOpen}
            onClose={() => {
              setTripSeparationModalOpen(false);
              setShipmentForSeparation(null);
              setSelectedShipmentIds([]);
            }}
            shipments={[
              shipmentForSeparation,
              ...shipmentForSeparation.otherShipmentsInTrip,
            ]}
            onRefresh={onRefresh}
            onShipmentsUncombine={onShipmentsUncombine}
          />
        ) : null}
      </Box>
    );
  }
);

export default PlanningGrid;

const renderForAugmentedShipment = (
  shipment: AugmentedShipment,
  renderFn: (shipment: AugmentedShipment) => ReactNode
) => {
  return (
    <Stack direction="row" spacing={1}>
      {[shipment, ...shipment.otherShipmentsInTrip].map((shipment, i) => (
        <>
          {i > 0 ? <Divider orientation="vertical" flexItem /> : null}
          {renderFn(shipment)}
        </>
      ))}
    </Stack>
  );
};
